import { useState } from "react";
import type { EtapaProceso } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Icono } from "../../components/Icono";
import { Insignia } from "../../components/Insignia";
import { Tarjeta } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { EditorAutomatizaciones, EditorChecklist, EditorDocumentosSugeridos } from "./EditoresEtapa";

export interface TogglesVisualizacion {
  responsables: boolean;
  contadores: boolean;
  automatizaciones: boolean;
  tiempoObjetivo: boolean;
  checklist: boolean;
}

// Tarjeta de una Etapa dentro del Diseñador Visual — reutiliza sin
// modificarlos los 3 editores ya existentes (checklist/documentos
// sugeridos/automatizaciones) montándolos al expandir, exactamente como
// ya hacía la Vista Tabla clásica de ProcesosTrabajoPage.tsx.
export function EtapaTarjetaVisual({
  etapa,
  indice,
  cantidadExpedientes,
  tiempoPromedioDias,
  vistaCompacta,
  mostrar,
  puedeAdministrar,
  soloLectura = false,
  draggable = false,
  arrastrando = false,
  sobreDestino = false,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onEditar,
  onDuplicar,
  onEliminar,
  onCambio,
}: {
  etapa: EtapaProceso;
  indice: number;
  cantidadExpedientes?: number;
  tiempoPromedioDias?: number | null;
  vistaCompacta: boolean;
  mostrar: TogglesVisualizacion;
  puedeAdministrar: boolean;
  soloLectura?: boolean;
  draggable?: boolean;
  arrastrando?: boolean;
  sobreDestino?: boolean;
  onDragStart?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: () => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onEditar?: () => void;
  onDuplicar?: () => void;
  onEliminar?: () => void;
  onCambio?: () => void;
}) {
  const [expandida, setExpandida] = useState(false);
  const { mostrar: mostrarToast } = useToast();

  const vencida =
    tiempoPromedioDias != null && etapa.tiempo_objetivo_dias != null && tiempoPromedioDias > etapa.tiempo_objetivo_dias;

  return (
    <div
      draggable={draggable}
      onDragStart={() => onDragStart?.()}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver?.(e);
      }}
      onDragLeave={() => onDragLeave?.()}
      onDrop={(e) => {
        e.preventDefault();
        onDrop?.(e);
      }}
      onDragEnd={() => onDragEnd?.()}
      className={`w-72 shrink-0 transition-opacity ${arrastrando ? "opacity-40" : ""}`}
    >
      <Tarjeta
        className={`p-4 ${sobreDestino ? "ring-2 ring-secundario" : ""} ${!etapa.activo ? "opacity-60" : ""}`}
      >
        <div className="flex items-start gap-2">
          {draggable && (
            <span className="mt-0.5 cursor-grab text-texto-secundario active:cursor-grabbing">
              <Icono nombre="arrastrar" className="h-4 w-4" />
            </span>
          )}
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-borde text-xs font-semibold text-texto-secundario"
            style={etapa.color ? { borderColor: etapa.color, color: etapa.color } : undefined}
          >
            {indice + 1}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-borde" style={{ backgroundColor: etapa.color ?? undefined }} />
              <h4 className="truncate text-sm font-semibold text-texto">{etapa.nombre}</h4>
            </div>
            {mostrar.responsables && (
              <p className="truncate text-xs text-texto-secundario">{etapa.responsable_nombre ?? "Sin responsable"}</p>
            )}
          </div>
          {!soloLectura && (
            <button
              type="button"
              onClick={() => setExpandida((v) => !v)}
              className="shrink-0 text-texto-secundario hover:text-texto"
              aria-label={expandida ? "Colapsar" : "Expandir"}
            >
              <Icono nombre="expandir-colapsar" className="h-4 w-4" />
            </button>
          )}
        </div>

        {!vistaCompacta && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {mostrar.contadores && cantidadExpedientes != null && (
              <Insignia texto={`${cantidadExpedientes} expediente${cantidadExpedientes === 1 ? "" : "s"}`} />
            )}
            {mostrar.tiempoObjetivo && (tiempoPromedioDias != null || etapa.tiempo_objetivo_dias != null) && (
              <Insignia
                texto={`${tiempoPromedioDias ?? "—"}${etapa.tiempo_objetivo_dias != null ? ` / ${etapa.tiempo_objetivo_dias}` : ""}d`}
                claveColor={vencida ? "Vencido" : "Cumplido"}
              />
            )}
            {mostrar.checklist && (etapa.checklist_count ?? 0) > 0 && <Insignia texto={`Checklist: ${etapa.checklist_count}`} />}
            {(etapa.documentos_count ?? 0) > 0 && <Insignia texto={`Docs: ${etapa.documentos_count}`} />}
            {mostrar.automatizaciones && (etapa.automatizaciones_count ?? 0) > 0 && (
              <Insignia texto={`Auto: ${etapa.automatizaciones_count}`} />
            )}
            {vencida && <Insignia texto="Cuello de botella" claveColor="Vencido" />}
          </div>
        )}

        {vistaCompacta && (
          <div className="mt-2 flex items-center gap-2 text-xs text-texto-secundario">
            {cantidadExpedientes != null && <span>{cantidadExpedientes} exp.</span>}
            {tiempoPromedioDias != null && <span>{tiempoPromedioDias}d</span>}
          </div>
        )}

        {!soloLectura && !vistaCompacta && (onEditar || onDuplicar || onEliminar) && puedeAdministrar && (
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-borde pt-2.5">
            {onEditar && (
              <Boton variante="secundario" tamano="sm" onClick={onEditar}>
                Editar
              </Boton>
            )}
            {onDuplicar && (
              <Boton variante="secundario" tamano="sm" onClick={onDuplicar}>
                Duplicar
              </Boton>
            )}
            {onEliminar && (
              <Boton variante="secundario" tamano="sm" onClick={onEliminar}>
                {etapa.activo ? "Eliminar" : "Reactivar"}
              </Boton>
            )}
          </div>
        )}

        {expandida && !soloLectura && (
          <div className="mt-3 space-y-4 border-t border-borde pt-3">
            <EditorChecklist
              etapaId={etapa.id}
              puedeAdministrar={puedeAdministrar}
              onError={(m) => mostrarToast(m, "error")}
              onExito={() => onCambio?.()}
            />
            <EditorDocumentosSugeridos
              etapaId={etapa.id}
              puedeAdministrar={puedeAdministrar}
              onError={(m) => mostrarToast(m, "error")}
              onExito={() => onCambio?.()}
            />
            <EditorAutomatizaciones
              etapaId={etapa.id}
              puedeAdministrar={puedeAdministrar}
              onError={(m) => mostrarToast(m, "error")}
              onExito={() => onCambio?.()}
            />
          </div>
        )}
      </Tarjeta>
    </div>
  );
}
